import { Box, CircularProgress, IconButton } from '@material-ui/core';
import {
  getAllDocumentationRequiredByLoanId,
  Loan,
  patchSyncBorrowerDocumentationByLoanId,
  patchUploadDocumentationToLender,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { useDocumentationHeaderActions } from '@/components/modules/Documentation/components/DocumentationHeader/hooks/useDocumentationHeaderActions';
import { DocumentManagerActions } from '@/components/modules/Documentation/components/DocumentationHeader/types/documentManagerActions';
import { DocumentationMode } from '@/components/modules/Documentation/types/DocumentationMode';
import { DocumentManagerStandardActionDialogParams } from '@/components/modules/Documentation/types/DocumentManagerStandardActionDialogParams';
import { Font } from '@/components/shared/components/Font';
import { IconHeader } from '@/components/shared/components/IconHeader';
import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { StandardActionDialog } from '@/components/shared/components/StandardActionDialog/StandardActionDialog';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

interface DocumentationHeaderProps {
  loan: Loan;
  mode: DocumentationMode;
}

export const DocumentationHeader = ({ loan, mode }: DocumentationHeaderProps) => {
  const { request, loading } = useRequest();
  const { revalidate, loading: revalidateLoading } = useCache(
    getAllDocumentationRequiredByLoanId,
    { loanId: loan.id },
    { revalidateOnMount: false },
  );

  const { opened, setOpened, toggleDialog } = useDialog();

  const [actionDialogParams, setActionDialogParams] = useState<DocumentManagerStandardActionDialogParams | undefined>();

  const loanApplicationId = loan.loanApplicationSelected?.id;

  const getActionDialogParamsByType = (actionType: DocumentManagerActions) => {
    switch (actionType) {
      case DocumentManagerActions.SEND_ALL_DOCUMENTS:
        return {
          actionType: DocumentManagerActions.SEND_ALL_DOCUMENTS,
          titleTextElement: <Text tId={'SEND_ALL_DOCUMENTS'} />,
          actionDescriptionTextElement: <Text tId={'SEND_ALL_DOCUMENTS_DESCRIPTION'} />,
          actionButtonsArray: [
            {
              onClick: async () => {
                if (!loanApplicationId) return null;
                await request(patchUploadDocumentationToLender, { id: loanApplicationId });
                await revalidate();
                setOpened(false);
              },
              buttonLabelTextId: 'SEND',
            },
          ],
        };
      case DocumentManagerActions.SYNCHRONIZE_DOCUMENTS:
        return {
          actionType: DocumentManagerActions.SYNCHRONIZE_DOCUMENTS,
          titleTextElement: <Text tId={'SYNCHRONIZE_DOCUMENTS'} />,
          actionDescriptionTextElement: <Text tId={'SYNCHRONIZE_DOCUMENTS_DESCRIPTION'} />,
          actionButtonsArray: [
            {
              onClick: async () => {
                await request(patchSyncBorrowerDocumentationByLoanId, { id: loan.id });
                await revalidate();
                setOpened(false);
              },
              buttonLabelTextId: 'SYNCHRONIZE',
            },
          ],
        };
      default:
        break;
    }
  };

  const openActionDialogByActionType = (actionType: DocumentManagerActions) => {
    const actionDialogParams = getActionDialogParamsByType(actionType);
    setActionDialogParams(actionDialogParams);
    toggleDialog();
  };

  const { getActionOptionsArray } = useDocumentationHeaderActions({
    onClickAction: openActionDialogByActionType,
    loanApplicationId,
  });

  const actionOptionsArray = getActionOptionsArray(mode);

  const showActions = mode === DocumentationMode.FULL && actionOptionsArray.length > 0;

  return (
    <>
      <Box display={'flex'} width={'100%'} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'inline-flex'} alignItems={'center'}>
          <IconHeader iconName={'description'} color={'secondary'} />
          <Font color={'title'} variant={'body2'}>
            <Text strong tId={'DOCUMENTATION_MANAGER'} />
          </Font>
        </Box>
        <Box>
          <IconButton disabled={revalidateLoading} onClick={() => revalidate()} style={{ padding: '5px' }}>
            {revalidateLoading ? (
              <CircularProgress size={19} color={'secondary'} />
            ) : (
              <i className={'fade-in-anim material-icons-outlined'} style={{ fontSize: '20px' }}>
                loop
              </i>
            )}
          </IconButton>
          {showActions && <MoreActionsMenuWithIcon actionsOptionsArray={actionOptionsArray} />}
        </Box>
      </Box>
      {actionDialogParams && (
        <StandardActionDialog
          opened={opened}
          setOpened={setOpened}
          titleTextElement={actionDialogParams.titleTextElement}
          actionDescriptionTextElement={actionDialogParams.actionDescriptionTextElement}
          actionButtonsArray={actionDialogParams.actionButtonsArray}
          setActionDialogParams={setActionDialogParams}
          loading={loading || revalidateLoading}
        />
      )}
    </>
  );
};
